import React from 'react';
import { styled } from '@compiled/react';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading';
import CheckCircleIcon from '@atlaskit/icon/core/migration/status-success--check-circle';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import providersListQueryOperation, {
	type providersListTooltipContentQuery,
} from '@atlassian/jira-relay/src/__generated__/providersListTooltipContentQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { SUPPORT_DOCS_LINK_URL } from '../../../common/constants.tsx';
import messages from './messages.tsx';
import { ProviderLogo } from './provider-logo/index.tsx';

export const useLoadProvidersListQuery = (): [
	PreloadedQuery<providersListTooltipContentQuery> | null | undefined,
	() => void,
] => {
	const [queryReference, loadQuery] = useQueryLoader<providersListTooltipContentQuery>(
		providersListQueryOperation,
	);

	const cloudId = useCloudId();

	const loadProvidersListQuery = () => {
		if (queryReference == null) {
			loadQuery({
				cloudId: createAri({
					resourceOwner: 'jira',
					resourceType: 'site',
					resourceId: cloudId,
				}),
			});
		}
	};

	return [queryReference, loadProvidersListQuery];
};

type InnerProps = {
	queryReference: PreloadedQuery<providersListTooltipContentQuery>;
};

export const ProvidersListInner = ({ queryReference }: InnerProps) => {
	const { devOps } = usePreloadedQuery<providersListTooltipContentQuery>(
		graphql`
			query providersListTooltipContentQuery($cloudId: ID!) {
				devOps @required(action: THROW) {
					providers(id: $cloudId, providerTypes: [DEPLOYMENT])
						@required(action: THROW)
						@optIn(to: "DevOpsSecurityInJira") {
						deploymentProviders @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							...providerLogo_softwareReleasesLaunchCard
						}
					}
				}
			}
		`,
		queryReference,
	);

	const { formatMessage } = useIntl();
	const { deploymentProviders } = devOps.providers;

	const renderProvidersConnectedMessage = () => {
		switch (deploymentProviders.length) {
			case 1:
				return formatMessage(messages.oneProviderIsConnected, {
					providerName: deploymentProviders[0]?.name,
				});
			case 2:
				return formatMessage(messages.twoProvidersAreConnected);
			default:
				return formatMessage(messages.threeOrMoreProvidersAreConnected);
		}
	};

	return (
		<Container>
			<ProviderList>
				{deploymentProviders
					.filter(Boolean)
					.slice(0, 3)
					.map((provider) => (
						<LogoContainer key={provider.id}>
							<ProviderLogo provider={provider} />
						</LogoContainer>
					))}
				<CheckIconContainer>
					<CheckCircleIcon spacing="spacious" color={token('color.icon.success')} label="" />
				</CheckIconContainer>
			</ProviderList>
			<Heading size="xsmall">{renderProvidersConnectedMessage()}</Heading>
			<DetailText>
				{formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? messages.setUpDeploymentsDetailIssueTerminologyRefreshM4Galaxia
						: messages.setUpDeploymentsDetailIssueTerminologyRefreshM4,
				)}
			</DetailText>
			<LinkText href={SUPPORT_DOCS_LINK_URL} target="_blank">
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.supportLinkTextIssueTerminologyRefreshM4
						: messages.supportLinkText,
				)}
			</LinkText>
		</Container>
	);
};

type Props = {
	queryReference: PreloadedQuery<providersListTooltipContentQuery>;
};

export const ProvidersList = (props: Props) => (
	<Placeholder
		name="providers-list-inner"
		fallback={
			<Container>
				<Spinner />
			</Container>
		}
	>
		<ProvidersListInner queryReference={props.queryReference} />
	</Placeholder>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	width: '276px',
	boxSizing: 'border-box',
	paddingTop: token('space.400'),
	paddingRight: token('space.400'),
	paddingBottom: token('space.400'),
	paddingLeft: token('space.400'),
	textAlign: 'center',

	color: token('color.text.subtlest'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProviderList = styled.ul({
	padding: '0',
	margin: '0',
	listStyleType: 'none',
	marginBottom: token('space.150'),
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoContainer = styled.li({
	display: 'inline-block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:nth-of-type(n+2)': {
		marginLeft: token('space.negative.150'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DetailText = styled.p({
	paddingTop: token('space.100'),
	paddingRight: 0,
	paddingBottom: token('space.300'),
	paddingLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CheckIconContainer = styled.li({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'absolute',
	width: '24px',
	height: '24px',

	backgroundColor: token('elevation.surface'),
	borderRadius: token('radius.full'),
	top: token('space.negative.050'),
	right: token('space.negative.050'),
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkText = styled.a({
	color: token('color.link'),
});
